import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Share2, X, ExternalLink, Copy, Crown, Circle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * FounderWMMSetupChecklist — dismissable dashboard widget that walks a
 * newly-provisioned Founder through the 3-step WeMillionairesMM setup:
 *
 *   1. ☑ WMM account provisioned (auto — user is a Founder)
 *   2. ☐ Paste your WMM profile URL (checked when microsite.wemm_profile_url set)
 *   3. ☐ Share your Digital Card (checked when Share button clicked)
 *
 * Auto-hides when all 3 done OR user clicks the X (dismissal persists
 * via /wemm/setup/dismiss on the backend). Only renders for Founders.
 *
 * The Share button uses navigator.share() when available (mobile native
 * sheet) and falls back to clipboard copy on desktop.
 */
export default function FounderWMMSetupChecklist({ getAuthHeader }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/wemm/setup/status`, { headers: getAuthHeader() });
        setStatus(res.data);
      } catch {
        // Non-fatal — the widget just doesn't render.
      }
    })();
  }, [getAuthHeader]);

  if (!status || !status.is_founder || status.dismissed_at || !visible) return null;

  const items = [
    {
      key: 'provisioned',
      done: !!status.provisioned,
      title: 'WMM account provisioned',
      subtitle: status.provisioned
        ? "You're active on WeMillionairesMM"
        : 'Being set up — check your email',
      action: null,
    },
    {
      key: 'url_set',
      done: !!status.url_set,
      title: 'Paste your WeMillionairesMM URL',
      subtitle: status.url_set
        ? 'Your Digital Card links to your WMM profile'
        : 'Profile Settings → Micro-site → WeMillionairesMM link',
      action: status.url_set
        ? null
        : {
            label: 'Open Settings',
            icon: ExternalLink,
            onClick: () => navigate('/profile/settings?tab=microsite'),
          },
    },
    {
      key: 'shared',
      done: !!status.shared,
      title: 'Share your Digital Card',
      subtitle: status.shared
        ? 'Your card has been shared at least once'
        : 'Get it in front of your first lead',
      action: status.shared
        ? null
        : {
            label: 'Share now',
            icon: Share2,
            onClick: () => shareCard(),
          },
    },
  ];

  const shareCard = async () => {
    if (!status.card_url) {
      toast.error('Card URL not ready yet — try again in a moment');
      return;
    }
    const shareData = {
      title: 'My WeMasterminds Digital Card',
      text: 'Add me on WeMasterminds',
      url: status.card_url,
    };
    try {
      // Prefer the OS share sheet on mobile — native contact + iMessage/WhatsApp options.
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share(shareData);
      } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(status.card_url);
        toast.success('Card link copied to clipboard');
      } else {
        window.open(status.card_url, '_blank');
      }
      // Mark as shared regardless of which path succeeded.
      await axios.post(`${API_URL}/api/wemm/setup/mark-shared`, {}, { headers: getAuthHeader() });
      setStatus((s) => (s ? { ...s, shared: true } : s));
    } catch (err) {
      // User cancelled the share sheet — no toast (it's a normal action).
      if (err?.name !== 'AbortError') {
        toast.error('Could not share the card');
      }
    }
  };

  const copyLink = async () => {
    if (!status.card_url) return;
    try {
      await navigator.clipboard.writeText(status.card_url);
      toast.success('Link copied');
      await axios.post(`${API_URL}/api/wemm/setup/mark-shared`, {}, { headers: getAuthHeader() });
      setStatus((s) => (s ? { ...s, shared: true } : s));
    } catch {
      toast.error('Copy failed');
    }
  };

  const dismiss = async () => {
    setVisible(false);
    try {
      await axios.post(`${API_URL}/api/wemm/setup/dismiss`, {}, { headers: getAuthHeader() });
    } catch {
      // Even if the dismiss call fails, the widget is already hidden for this session.
    }
  };

  const doneCount = items.filter((i) => i.done).length;
  const complete = doneCount === items.length;

  // If they've done all 3, celebrate briefly then auto-hide once dismissed.
  return (
    <div
      className="relative rounded-2xl p-5 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent border border-amber-500/25 overflow-hidden"
      data-testid="founder-wmm-setup-checklist"
    >
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/5 transition"
        aria-label="Dismiss checklist"
        data-testid="wmm-setup-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
          <Crown className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold">Founder onboarding</p>
          <h3 className="text-lg font-playfair text-white">
            {complete ? 'You are all set 🎉' : 'Complete your WMM setup'}
          </h3>
        </div>
        <div className="ml-auto text-right">
          <p className="text-2xl font-playfair text-amber-300 leading-none" data-testid="wmm-setup-count">
            {doneCount}<span className="text-sm text-white/40">/{items.length}</span>
          </p>
        </div>
      </div>

      <ol className="space-y-2">
        {items.map((item) => (
          <li
            key={item.key}
            className={`flex items-start gap-3 p-3 rounded-lg border transition ${
              item.done ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-white/[0.02] border-white/5'
            }`}
            data-testid={`wmm-item-${item.key}`}
          >
            <div className="mt-0.5 flex-shrink-0">
              {item.done ? (
                <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Check className="w-3 h-3 text-black" />
                </div>
              ) : (
                <Circle className="w-5 h-5 text-white/30" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${item.done ? 'text-emerald-200 line-through decoration-emerald-500/40' : 'text-white'}`}>
                {item.title}
              </p>
              <p className="text-xs text-white/50 mt-0.5">{item.subtitle}</p>
            </div>
            {item.action && (
              <button
                onClick={item.action.onClick}
                className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-brand-gold/15 border border-brand-gold/30 text-brand-gold text-xs font-medium hover:bg-brand-gold/25 transition"
                data-testid={`wmm-action-${item.key}`}
              >
                <item.action.icon className="w-3 h-3" />
                {item.action.label}
              </button>
            )}
          </li>
        ))}
      </ol>

      {status.card_url && !complete && (
        <div className="mt-4 flex items-center gap-2 text-[11px] text-white/50">
          <span className="truncate">Card: <span className="text-white/70">{status.card_url}</span></span>
          <button
            onClick={copyLink}
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded border border-white/10 hover:bg-white/5 text-white/70"
            data-testid="wmm-copy-link"
            title="Copy card link"
          >
            <Copy className="w-3 h-3" /> Copy
          </button>
        </div>
      )}
    </div>
  );
}
